import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Keeps a stable selected option object when the form stores only an id.
 */
export default function useResolvedSelectValue({
  valueId = null,
  selectedSeed = null,
  mapToOption,
  isSameOption,
}) {
  const [selected, setSelected] = useState(null);
  const [userCleared, setUserCleared] = useState(false);

  useEffect(() => {
    if (selectedSeed?.id) {
      setUserCleared(false);
      setSelected((prev) => {
        const next = mapToOption(selectedSeed) ?? selectedSeed;
        if (prev?.id != null && isSameOption(prev, next)) return prev;
        return next;
      });
      return;
    }
    if (!valueId) {
      setSelected(null);
      if (!selectedSeed) {
        setUserCleared(false);
      }
    }
  }, [isSameOption, mapToOption, selectedSeed, valueId]);

  const resolvedSelected = useMemo(() => {
    if (selected) return selected;
    if (userCleared) return null;
    if (selectedSeed) {
      return mapToOption(selectedSeed) ?? selectedSeed;
    }
    return null;
  }, [mapToOption, selected, selectedSeed, userCleared]);

  const resolveOptionFromList = useCallback(
    (options) => {
      if (!resolvedSelected?.id) return null;
      return (
        options.find((option) => isSameOption(option, resolvedSelected)) ?? resolvedSelected
      );
    },
    [isSameOption, resolvedSelected]
  );

  const bindChange = useCallback(
    (onChange) => (option) => {
      setUserCleared(option == null);
      setSelected(option ?? null);
      onChange?.(option);
    },
    []
  );

  return {
    resolvedSelected,
    resolveOptionFromList,
    bindChange,
  };
}
